<template>
  <main class="flex-grow container mx-auto p-4 sm:p-6">
    <div class="text-center">
      <!-- Loading Spinner -->
      <div v-if="isLoading" class="flex justify-center items-center h-64">
        <div class="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-indigo-600"></div>
      </div>

      <!-- Dashboard Content -->
      <div v-else>
        <h2 class="text-2xl sm:text-4xl font-bold mb-4 text-slate-900 dark:text-white">Welcome, {{ fname }}</h2>

        <!-- Weekend Mode Indicator -->
        <div v-if="isWeekend" class="mb-6 flex justify-center">
          <span class="inline-flex items-center gap-2 px-4 py-2 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg font-medium">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            Weekend Mode
          </span>
        </div>

        <!-- Main Layout: On weekends, swap columns so assignments (longer) are on left -->
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <!-- WEEKEND: Left Column = Assignments -->
          <div v-if="isWeekend" class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
            <h3 class="text-2xl sm:text-3xl font-semibold mb-6 text-slate-900 dark:text-white">{{ assignmentsHeading }}</h3>
            <div v-if="groupedAssignments.length" class="space-y-5">
              <div v-for="group in groupedAssignments" :key="group.dateKey">
                <!-- Date Header -->
                <div class="mb-2">
                  <span class="inline-block text-base font-semibold text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-slate-800 px-4 py-1.5 rounded-md">
                    {{ group.dateLabel }}
                  </span>
                </div>
                <!-- Assignments for this date -->
                <div class="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-slate-50/50 dark:bg-slate-800/30">
                  <ul>
                    <li
                        v-for="(assignment, index) in group.assignments"
                        :key="assignment.assignment_id"
                        :class="[
                          'py-4 px-4 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200',
                          index !== group.assignments.length - 1 ? 'border-b border-slate-200 dark:border-slate-700' : ''
                        ]"
                        @click="openAssignmentModal(assignment)"
                    >
                      <div class="flex flex-col justify-between items-start text-slate-800 dark:text-slate-100">
                        <div class="flex-grow">
                          <span class="font-semibold">{{ assignment.section_name }}: </span>
                          <span>{{ assignment.short_description }}</span>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            <div v-else class="text-center py-4">
              <p class="text-slate-600 dark:text-slate-400">No assignments due this week.</p>
            </div>
          </div>

          <!-- WEEKDAY: Left Column = Schedule -->
          <div v-else class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
            <h3 class="text-2xl sm:text-3xl font-semibold mb-6 text-slate-900 dark:text-white">{{ scheduleHeading }}</h3>
            <div v-if="todaySchedules.length" class="overflow-x-auto">
              <table class="min-w-full table-auto text-slate-800 dark:text-slate-100">
                <thead>
                <tr class="bg-slate-100 dark:bg-slate-800">
                  <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Time</th>
                  <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Class</th>
                  <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Room</th>
                </tr>
                </thead>
                <tbody>
                <tr
                    v-for="schedule in todaySchedules"
                    :key="schedule.section_id"
                    class="border-b border-slate-200 dark:border-slate-700 even:bg-slate-50 dark:even:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
                >
                  <td class="px-3 sm:px-5 py-8 text-sm sm:text-base align-top">
                    {{ formatTime(schedule.start_time) }} - {{ formatTime(schedule.end_time) }}
                  </td>
                  <td class="px-3 sm:px-5 py-8 text-sm sm:text-base font-medium align-top">{{ schedule.course_title }}</td>
                  <td class="px-3 sm:px-5 py-8 text-sm sm:text-base align-top">{{ schedule.room_name }}</td>
                </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="text-center py-8">
              <p class="text-slate-600 dark:text-slate-400">No schedule available for today.</p>
            </div>
          </div>

          <!-- Right Column: Schedule on weekend, Assignments+Lunch on weekday -->
          <div class="space-y-6">
            <!-- WEEKEND: Schedule -->
            <div v-if="isWeekend" class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
              <h3 class="text-2xl sm:text-3xl font-semibold mb-6 text-slate-900 dark:text-white">{{ scheduleHeading }}</h3>
              <div v-if="todaySchedules.length" class="overflow-x-auto">
                <table class="min-w-full table-auto text-slate-800 dark:text-slate-100">
                  <thead>
                  <tr class="bg-slate-100 dark:bg-slate-800">
                    <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Time</th>
                    <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Class</th>
                    <th class="px-3 sm:px-5 py-5 text-left text-slate-800 dark:text-slate-300 text-base font-semibold">Room</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr
                      v-for="schedule in todaySchedules"
                      :key="schedule.section_id"
                      class="border-b border-slate-200 dark:border-slate-700 even:bg-slate-50 dark:even:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-150"
                  >
                    <td class="px-3 sm:px-5 py-8 text-sm sm:text-base align-top">
                      {{ formatTime(schedule.start_time) }} - {{ formatTime(schedule.end_time) }}
                    </td>
                    <td class="px-3 sm:px-5 py-8 text-sm sm:text-base font-medium align-top">{{ schedule.course_title }}</td>
                    <td class="px-3 sm:px-5 py-8 text-sm sm:text-base align-top">{{ schedule.room_name }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="text-center py-8">
                <p class="text-slate-600 dark:text-slate-400">No schedule available for Monday.</p>
              </div>
            </div>

            <!-- WEEKDAY: Assignments -->
            <div v-else class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
              <h3 class="text-2xl sm:text-3xl font-semibold mb-6 text-slate-900 dark:text-white">{{ assignmentsHeading }}</h3>
              <ul v-if="todayAssignments.length">
                <li
                    v-for="assignment in todayAssignments"
                    :key="assignment.assignment_id"
                    class="border-b border-slate-300 dark:border-slate-700 py-4 px-4 last:border-b-0 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors duration-200"
                    @click="openAssignmentModal(assignment)"
                >
                  <div class="flex flex-col justify-between items-start text-slate-800 dark:text-slate-100">
                    <div class="flex-grow">
                      <span class="font-semibold">{{ assignment.section_name }}: </span>
                      <span>{{ assignment.short_description }}</span>
                    </div>
                  </div>
                </li>
              </ul>
              <div v-else class="text-center py-4">
                <p class="text-slate-600 dark:text-slate-400">No assignments due today.</p>
              </div>
            </div>

            <!-- Lunch Menu (both weekend and weekday) -->
            <div class="bg-white/80 dark:bg-slate-900/80 backdrop-blur-sm shadow-xl rounded-2xl p-6 sm:p-8 border border-slate-200/50 dark:border-slate-700/50 transition-colors duration-300">
              <h3 class="text-2xl sm:text-3xl font-semibold mb-6 text-slate-900 dark:text-white">{{ lunchHeading }}</h3>

              <!-- Loading state -->
              <div v-if="diningLoading" class="flex justify-center items-center h-24">
                <div class="animate-spin rounded-full h-10 w-10 border-t-4 border-b-4 border-indigo-600"></div>
              </div>

              <!-- Error state -->
              <div v-else-if="diningError" class="text-slate-600 dark:text-slate-400">
                {{ diningError }}
              </div>

              <!-- No menu state -->
              <div v-else-if="!dining || !dining.has_menu" class="text-slate-600 dark:text-slate-400">
                {{ isWeekend ? 'No lunch menu available for Monday.' : 'No lunch menu available for today.' }}
              </div>

              <!-- Menu display (compact lunch summary) -->
              <div v-else>
                <div v-if="dashboardLunchSections.length" class="space-y-4 mb-4">
                  <div v-for="(section, idx) in dashboardLunchSections" :key="idx">
                    <h4 class="text-lg font-semibold mb-3 text-slate-900 dark:text-slate-100">{{ section.name }}</h4>
                    <ul class="space-y-2">
                      <li v-for="(item, iidx) in section.items" :key="iidx" class="text-base text-slate-800 dark:text-slate-100">
                        <span class="font-medium">{{ item.name }}</span>
                        <span v-if="item.calories" class="text-sm text-slate-500 dark:text-slate-400 ml-2">
                          {{ Math.round(item.calories) }} kcal
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>

                <!-- Pizza Station Status -->
                <div v-if="showPizzaStationStatus" class="flex items-center justify-center gap-2 mb-5 text-sm">
                  <span class="text-slate-600 dark:text-slate-400">Pizza Station</span>
                  <span v-if="isPizzaStationOpen" class="flex items-center gap-1 text-green-600 dark:text-green-400 font-medium">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                    </svg>
                    <span>Open</span>
                  </span>
                  <span v-else class="flex items-center gap-1 text-red-600 dark:text-red-400 font-medium">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                    <span>Closed</span>
                  </span>
                </div>

                <!-- View Full Menu Button -->
                <router-link
                    to="/menu"
                    class="inline-block w-full text-center bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2.5 rounded-lg font-medium shadow-md transition duration-300"
                >
                  View Full Menu
                </router-link>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Assignment Description Modal -->
      <div
          v-if="showAssignmentModal"
          class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4 overflow-y-auto"
          @wheel.stop
      >
        <div
            class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-2xl w-full border border-slate-200 dark:border-slate-700 transition-colors duration-300 relative max-h-[85vh] overflow-hidden"
            @click.stop
            @touchmove.stop
        >
          <button
              @click="closeModal"
              class="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <h3 class="text-2xl font-bold mb-1 text-slate-900 dark:text-white break-words pr-12">
            {{ currentAssignment.section_name }}
          </h3>
          <p class="text-base font-semibold text-slate-500 dark:text-slate-400 mb-6">
            {{ currentAssignment.short_description }}
          </p>

          <!-- Scrollable long description -->
          <div class="desc-scroll text-slate-700 dark:text-slate-300 mb-6 whitespace-pre-wrap break-words max-h-[45vh] overflow-y-auto pr-2 leading-relaxed">
            {{ currentAssignment.long_description }}
          </div>

          <!-- Due date with class start time -->
          <div class="flex items-center gap-2 text-slate-600 dark:text-slate-400 mb-6">
            <svg class="w-5 h-5 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
            </svg>
            <span class="text-base">
              Due: {{ formatModalDueDate(currentAssignment.date_due) }}
              <span v-if="currentAssignment.classStartTime" class="text-slate-500 dark:text-slate-500">
                (class at {{ currentAssignment.classStartTime }})
              </span>
            </span>
          </div>

          <div class="flex justify-end pt-6 border-t border-slate-200 dark:border-slate-700">
            <button
                @click="closeModal"
                class="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>

      <!-- Add Custom Task Modal -->
      <div
          v-if="showAddTaskModal"
          class="fixed inset-0 flex items-center justify-center z-50 bg-black/50 backdrop-blur-sm p-4"
          @wheel.stop
          @touchmove.prevent
      >
        <div
            class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl p-8 max-w-lg w-full border border-slate-200 dark:border-slate-700 transition-colors duration-300 relative max-h-[85vh] overflow-y-auto"
            @click.stop
        >
          <h3 class="text-2xl font-bold mb-2 text-slate-900 dark:text-white">Add Custom Task</h3>
          <p class="text-sm text-slate-500 dark:text-slate-400 mb-6">Fill in the details for your custom assignment</p>
          <form @submit.prevent="addTask">
            <div class="mb-4">
              <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">Section Name</label>
              <input type="text" v-model="newTask.section_name" class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" placeholder="e.g., Math 101" required />
            </div>
            <div class="mb-4">
              <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">Short Description</label>
              <input type="text" v-model="newTask.short_description" class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" placeholder="e.g., Homework Assignment" required />
            </div>
            <div class="mb-4">
              <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">Due Date</label>
              <input type="date" v-model="newTask.date_due" class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all" required />
            </div>
            <div class="mb-6">
              <label class="block text-slate-700 dark:text-slate-300 mb-2 font-medium text-sm">Long Description</label>
              <textarea v-model="newTask.long_description" class="w-full px-4 py-2.5 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all resize-none" placeholder="Detailed description..." rows="4" required></textarea>
            </div>
            <div class="flex justify-end gap-3">
              <button type="button" @click="closeAddTaskModal" class="bg-slate-200 hover:bg-slate-300 dark:bg-slate-700 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200">
                Cancel
              </button>
              <button type="submit" class="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200">
                Add Task
              </button>
            </div>
          </form>
        </div>
      </div>

      <!-- Changelog/Update Modal (commented out)
      <div
          v-if="showChangelogModal"
          class="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm p-4"
          @wheel.stop
      >
        <div
            class="changelog-modal bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-2xl w-full border border-slate-200 dark:border-slate-700 transition-colors duration-300 relative max-h-[85vh] overflow-hidden"
            @click.stop
        >
          <div class="relative px-8 pt-8 pb-6 border-b border-slate-100 dark:border-slate-800">
            <div class="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-purple-500/5 to-transparent dark:from-indigo-500/10 dark:via-purple-500/10"></div>
            <button
                @click="closeChangelogModal"
                class="absolute top-6 right-6 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors z-10"
            >
              <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <div class="relative">
              <div class="flex items-center gap-3 mb-2">
                <span class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-indigo-100 dark:bg-indigo-900/50 text-indigo-700 dark:text-indigo-300">
                  v2.0
                </span>
                <span class="text-sm text-slate-500 dark:text-slate-400">January 2026</span>
              </div>
              <h3 class="text-2xl font-bold text-slate-900 dark:text-white">What's New in BetterConnect</h3>
            </div>
          </div>
          <div class="changelog-content px-8 py-6 overflow-y-auto max-h-[calc(85vh-180px)] space-y-4">
            <div class="feature-card group p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 hover:border-indigo-200 dark:hover:border-indigo-800 transition-all duration-200">
              <div class="flex gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-sm">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="text-base font-semibold text-slate-900 dark:text-white mb-1">Integrated Lunch Menu</h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">View what's for lunch right from your dashboard.</p>
                </div>
              </div>
            </div>
            <div class="feature-card group p-4 rounded-xl bg-slate-50 dark:bg-slate-800/50 border border-slate-100 dark:border-slate-700/50 hover:border-indigo-200 dark:hover:border-indigo-800 transition-all duration-200">
              <div class="flex gap-4">
                <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center shadow-sm">
                  <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                  </svg>
                </div>
                <div class="flex-1 min-w-0">
                  <h4 class="text-base font-semibold text-slate-900 dark:text-white mb-1">Weekend Mode</h4>
                  <p class="text-sm text-slate-600 dark:text-slate-400">Plan your week ahead. See Monday's schedule and the full week's assignments on weekends.</p>
                </div>
              </div>
            </div>
            <div class="mt-2 p-4 rounded-xl bg-slate-50/50 dark:bg-slate-800/30 border border-slate-100 dark:border-slate-700/50">
              <h4 class="text-sm font-semibold text-slate-700 dark:text-slate-300 uppercase tracking-wide mb-3">Also in this update</h4>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
                <div class="flex items-center gap-2.5 text-slate-600 dark:text-slate-400">
                  <svg class="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Complete visual redesign</span>
                </div>
                <div class="flex items-center gap-2.5 text-slate-600 dark:text-slate-400">
                  <svg class="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>250% faster loading</span>
                </div>
                <div class="flex items-center gap-2.5 text-slate-600 dark:text-slate-400">
                  <svg class="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Improved custom assignments</span>
                </div>
                <div class="flex items-center gap-2.5 text-slate-600 dark:text-slate-400">
                  <svg class="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                  <span>Dozens of bug fixes</span>
                </div>
              </div>
            </div>
            <a
                href="https://apps.apple.com/us/app/betterconnect/id6757120274"
                target="_blank"
                rel="noopener noreferrer"
                class="flex items-center gap-4 p-4 rounded-xl border border-indigo-200 dark:border-indigo-800/50 bg-indigo-50/50 dark:bg-indigo-950/20 hover:bg-indigo-100/50 dark:hover:bg-indigo-900/30 transition-colors duration-200"
            >
              <div class="flex-shrink-0 w-10 h-10 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 flex items-center justify-center">
                <svg class="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
                </svg>
              </div>
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-slate-900 dark:text-white">Pre-order the iOS app</p>
              </div>
              <svg class="w-4 h-4 text-indigo-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
            </a>
          </div>
          <div class="px-8 py-5 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30">
            <button
                @click="closeChangelogModal"
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
            >
              Let's go
            </button>
          </div>
        </div>
      </div>
      -->
    </div> <!-- /text-center -->
  </main>
</template>

<script>
import axios from 'axios';
import he from 'he';
import sanitizeHtml from 'sanitize-html';
import { getDiningForDate } from '@/data/diningClient.js';
import Swal from 'sweetalert2';
import { getActiveAuthToken, redirectToLogin as beginLogin } from '@/config/auth';
import { backendUrl } from '@/config/backend';
import { getSchoolConfig, shouldHideMenuSection, useSchoolContext } from '@/config/schools';

const { activeSchoolId } = useSchoolContext();

export default {
  data() {
    return {
      token: null,
      fname: '',
      showAssignmentModal: false,
      currentAssignment: null,
      assignments: [],
      schedules: [],
      isLoading: false,
      showAddTaskModal: false,
      showChangelogModal: false,
      newTask: {
        section_name: '',
        short_description: '',
        date_due: '',
        long_description: '',
      },
      // Dining
      dining: null,
      diningLoading: false,
      diningError: '',

      statuses: [
        { value: 'not-started', label: 'Not Started', activeColorClass: 'text-red-500', inactiveClass: 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
        { value: 'in-progress', label: 'In Progress', activeColorClass: 'text-yellow-500', inactiveClass: 'bg-gray-300 text-gray-700 dark:bg-gray-700 dark:text-gray-300' },
        { value: 'complete', label: 'Complete', activeColorClass: 'text-green-500', inactiveClass: 'bg-gray-300 text-gray-700 dark:text-gray-300' },
      ],

      // Scroll locking
      savedScrollY: 0,

      // The changelog markup is currently disabled in the template, so keep
      // the state logic disabled as well to avoid locking page scroll.
      CHANGELOG_ENABLED: false,
      CHANGELOG_VERSION: '2.0',

      // Cache for schedules by date (to look up class start times)
      schedulesCache: {},
      scheduleRequests: {},
      schedulePrefetchQueued: false,
    };
  },
  computed: {
    schoolConfig() {
      return getSchoolConfig(activeSchoolId.value);
    },

    isWeekend() {
      // Check if it's weekend in Eastern timezone
      const now = new Date();
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        weekday: 'short'
      });
      const weekdayStr = easternFormatter.format(now);
      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayOfWeek = weekdayMap[weekdayStr];
      return dayOfWeek === 0 || dayOfWeek === 6;
    },

    nextSchoolDay() {
      // Get current date in Eastern timezone
      const now = new Date();
      const easternFormatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        weekday: 'short'
      });

      const parts = easternFormatter.formatToParts(now);
      const getPart = (type) => parts.find(p => p.type === type)?.value;

      const yearET = parseInt(getPart('year'));
      const monthET = parseInt(getPart('month')) - 1;
      const dayET = parseInt(getPart('day'));
      const weekdayStr = getPart('weekday');

      const weekdayMap = { 'Sun': 0, 'Mon': 1, 'Tue': 2, 'Wed': 3, 'Thu': 4, 'Fri': 5, 'Sat': 6 };
      const dayOfWeek = weekdayMap[weekdayStr];

      let daysToAdd = 1;
      if (dayOfWeek === 6) daysToAdd = 2; // Saturday -> Monday
      else if (dayOfWeek === 0) daysToAdd = 1; // Sunday -> Monday

      // Create date at noon UTC (to avoid day boundary issues)
      return new Date(Date.UTC(yearET, monthET, dayET + daysToAdd, 17, 0, 0, 0));
    },

    scheduleHeading() {
      if (this.isWeekend) {
        const day = this.nextSchoolDay.toLocaleDateString('en-US', {
          weekday: 'long',
          timeZone: 'America/New_York'
        });
        return `${day}'s Schedule`;
      }
      return "Today's Schedule";
    },

    assignmentsHeading() {
      if (this.isWeekend) {
        return "This Week's Assignments";
      }
      return "Today's Assignments";
    },

    lunchHeading() {
      if (this.isWeekend) {
        const day = this.nextSchoolDay.toLocaleDateString('en-US', {
          weekday: 'long',
          timeZone: 'America/New_York'
        });
        return `${day}'s Lunch`;
      }
      return "Today's Lunch";
    },

    swalDarkModeConfig() {
      return {
        buttonsStyling: false,
        customClass: {
          popup: 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl',
          title: 'text-slate-900 dark:text-white',
          htmlContainer: 'text-slate-700 dark:text-slate-300',
          confirmButton: 'bg-indigo-600 hover:bg-indigo-700 text-white font-semibold py-2 px-4 rounded-lg ml-2',
          cancelButton: 'bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-900 dark:text-white font-semibold py-2 px-4 rounded-lg mr-2',
          actions: 'gap-2'
        }
      };
    },
    visibleDiningSections() {
      if (!this.dining?.sections) return [];

      return this.dining.sections
          .filter(section => !shouldHideMenuSection(section.name, this.schoolConfig.id))
          .map(section => ({ ...section, items: (section.items || []).slice() }))
          .filter(section => section.items.length > 0);
    },

    dashboardLunchSections() {
      if (!this.visibleDiningSections.length) return [];

      if (this.schoolConfig.id === 'collegiate') {
        const normalize = (value = '') => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
        const mainSections = this.visibleDiningSections.filter(section => normalize(section.name).includes('main entree'));
        return mainSections.length ? mainSections : this.visibleDiningSections.slice(0, 2);
      }

      return this.visibleDiningSections.slice(0, 3);
    },

    showPizzaStationStatus() {
      return this.schoolConfig.id === 'collegiate' && this.dining?.has_menu;
    },

    // Check if Pizza Station is available today
    isPizzaStationOpen() {
      if (!this.showPizzaStationStatus) return false;

      return this.visibleDiningSections.some(section =>
          section.items?.some(item =>
              item.name && item.name.toLowerCase().includes('pizza station')
          )
      );
    },

    todayAssignments() {
      if (this.isWeekend) {
        // Show assignments for the upcoming week (next 7 days from next school day)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const endDate = new Date(today);
        endDate.setDate(today.getDate() + 7);

        const filtered = this.assignments
            .filter(a => {
              const dueDateStr = a.date_due.split('T')[0];
              const [y, m, d] = dueDateStr.split('-').map(Number);
              const dueDate = new Date(y, m - 1, d);
              dueDate.setHours(0, 0, 0, 0);
              return dueDate >= today && dueDate <= endDate;
            })
            .sort((a, b) => new Date(a.date_due) - new Date(b.date_due));

        console.log('[Weekend Mode] Total assignments:', this.assignments.length);
        console.log('[Weekend Mode] Filtered assignments (next 7 days):', filtered.length);
        console.log('[Weekend Mode] Date range:', today.toDateString(), 'to', endDate.toDateString());

        return filtered;
      } else {
        // Show today's assignments
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayStr = this.formatApiDate(today);
        return this.assignments
            .filter(a => {
              const dueDate = a.date_due.split('T')[0];
              return dueDate === todayStr;
            })
            .sort((a, b) => new Date(a.date_due) - new Date(b.date_due));
      }
    },

    groupedAssignments() {
      if (!this.isWeekend) return [];

      // Group assignments by due date
      const groups = new Map();

      for (const assignment of this.todayAssignments) {
        const dueDateStr = assignment.date_due.split('T')[0];
        if (!groups.has(dueDateStr)) {
          groups.set(dueDateStr, []);
        }
        groups.get(dueDateStr).push(assignment);
      }

      // Convert to array and format date labels
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const result = [];
      for (const [dateKey, assignments] of groups.entries()) {
        const [y, m, d] = dateKey.split('-').map(Number);
        const dueDate = new Date(y, m - 1, d);
        dueDate.setHours(0, 0, 0, 0);

        // Format the date label
        let dateLabel;
        const tomorrow = new Date(today);
        tomorrow.setDate(today.getDate() + 1);

        if (dueDate.getTime() === today.getTime()) {
          dateLabel = 'Today';
        } else if (dueDate.getTime() === tomorrow.getTime()) {
          dateLabel = 'Tomorrow';
        } else {
          // Show full date: "Monday, December 9"
          dateLabel = dueDate.toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric'
          });
        }

        result.push({
          dateKey,
          dateLabel,
          assignments
        });
      }

      return result;
    },
    todaySchedules() {
      const targetDate = this.isWeekend ? this.nextSchoolDay : new Date();
      targetDate.setHours(0, 0, 0, 0);
      return this.schedules
          .filter(s => s.start_time.split('T')[0] === this.formatApiDate(targetDate))
          .sort((a, b) => new Date(a.start_time) - new Date(b.start_time));
    },

    // Any modal open?
    anyModalOpen() {
      return this.showAssignmentModal || this.showAddTaskModal || (this.CHANGELOG_ENABLED && this.showChangelogModal);
    },
  },

  watch: {
    anyModalOpen(isOpen) {
      if (isOpen) this.lockBodyScroll();
      else this.unlockBodyScroll();
    },
  },

  methods: {
    // ===== Scroll lock helpers =====
    lockBodyScroll() {
      this.savedScrollY = window.scrollY || window.pageYOffset || 0;
      const body = document.body;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
      body.style.overflow = 'hidden';
    },
    unlockBodyScroll() {
      const body = document.body;
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      body.style.overflow = '';
      window.scrollTo(0, this.savedScrollY || 0);
    },

    async fetchDining(isoDate) {
      this.diningError = '';
      this.diningLoading = true;

      try {
        console.group('[DINING][FE] fetchDining');
        console.log('Requesting date:', isoDate);
        this.dining = await getDiningForDate(isoDate, this.schoolConfig.id);
        console.groupEnd();
      } catch (e) {
        console.groupEnd();
        console.error('[DINING][FE] Error fetching dining:', {
          message: e?.message,
          status: e?.status,
        });
        this.diningError = e?.message || `Failed to load dining menu. (HTTP ${e?.status || 'N/A'})`;
        this.dining = {
          date: isoDate,
          has_menu: false,
          sections: []
        };
      } finally {
        this.diningLoading = false;
      }
    },

    cleanField(html) {
      if (html == null) return '';
      let withLineBreaks = String(html).replace(/<br\s*\/?>/gi, '\n').replace(/<\/?p>/gi, '\n');
      for (let i = 0; i < 5; i += 1) {
        const next = he.decode(withLineBreaks);
        if (next === withLineBreaks) break;
        withLineBreaks = next;
      }
      const cleanText = sanitizeHtml(withLineBreaks, { allowedTags: [], allowedAttributes: {} });
      return he.decode(cleanText).trim();
    },

    cleanAssignmentDescription(html) {
      return this.cleanField(html);
    },

    queueSchedulePrefetch() {
      if (this.schedulePrefetchQueued) return;
      this.schedulePrefetchQueued = true;

      const runPrefetch = () => {
        this.schedulePrefetchQueued = false;
        this.prefetchSchedulesForAssignments();
      };

      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        window.requestIdleCallback(runPrefetch, { timeout: 1500 });
      } else {
        setTimeout(runPrefetch, 0);
      }
    },

    prefetchSchedulesForAssignments() {
      if (!this.assignments.length) return;

      const uniqueDates = new Set();
      for (const assignment of this.assignments) {
        const dateStr = assignment?.date_due?.split('T')[0];
        if (dateStr) uniqueDates.add(dateStr);
      }

      for (const dateStr of uniqueDates) {
        if (!Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
          this.fetchScheduleForDate(dateStr);
        }
      }
    },

    async fetchScheduleForDate(dateStr) {
      // Check cache first
      if (Object.prototype.hasOwnProperty.call(this.schedulesCache, dateStr)) {
        return this.schedulesCache[dateStr];
      }

      if (this.scheduleRequests[dateStr]) {
        return this.scheduleRequests[dateStr];
      }

      // Fetch schedule for the specific date
      const request = axios.get(backendUrl('/schedule'), {
        params: { start_date: dateStr, end_date: dateStr },
        headers: { Authorization: `Bearer ${this.token}` },
      })
          .then((response) => {
            this.schedulesCache[dateStr] = response.data.value || [];
            return this.schedulesCache[dateStr];
          })
          .catch((error) => {
            console.error('Error fetching schedule for date:', dateStr, error);
            return [];
          })
          .finally(() => {
            delete this.scheduleRequests[dateStr];
          });

      this.scheduleRequests[dateStr] = request;
      return request;
    },

    getClassStartTimeFromSchedules(schedules, sectionName) {
      // Try to match the assignment's section_name to a schedule's course_title
      // Use case-insensitive matching and handle partial matches
      const normalizedSectionName = sectionName.toLowerCase().trim();

      for (const schedule of schedules) {
        const normalizedCourseTitle = (schedule.course_title || '').toLowerCase().trim();
        // Check for exact match or if one contains the other
        if (normalizedCourseTitle === normalizedSectionName ||
            normalizedCourseTitle.includes(normalizedSectionName) ||
            normalizedSectionName.includes(normalizedCourseTitle)) {
          return this.formatTime(schedule.start_time);
        }
      }
      return null;
    },

    async openAssignmentModal(assignment) {
      const cleanedDescription = this.cleanAssignmentDescription(assignment.long_description);
      const assignmentData = { ...assignment, long_description: cleanedDescription, classStartTime: null };

      this.currentAssignment = assignmentData;
      this.showAssignmentModal = true;

      // Get the due date in YYYY-MM-DD format
      const dueDateStr = assignment.date_due.split('T')[0];

      // Try to get class start time from cached schedules or fetch if needed
      const schedules = await this.fetchScheduleForDate(dueDateStr);
      const classStartTime = this.getClassStartTimeFromSchedules(schedules, assignment.section_name);

      if (this.currentAssignment &&
          this.currentAssignment.assignment_id === assignment.assignment_id) {
        this.currentAssignment = { ...this.currentAssignment, classStartTime };
      }
    },

    closeModal() {
      this.showAssignmentModal = false;
      this.currentAssignment = null;
    },

    openAddTaskModal() {
      this.showAddTaskModal = true;
    },

    closeAddTaskModal() {
      this.showAddTaskModal = false;
      this.newTask = { section_name: '', short_description: '', date_due: '', long_description: '' };
    },

    checkChangelogVersion() {
      if (!this.CHANGELOG_ENABLED) {
        this.showChangelogModal = false;
        localStorage.setItem('changelog_version', this.CHANGELOG_VERSION);
        return;
      }

      const seenVersion = localStorage.getItem('changelog_version');
      if (seenVersion !== this.CHANGELOG_VERSION) {
        this.showChangelogModal = true;
      }
    },

    closeChangelogModal() {
      this.showChangelogModal = false;
      localStorage.setItem('changelog_version', this.CHANGELOG_VERSION);
    },

    async addTask() {
      const dateParts = this.newTask.date_due.split('-');
      const assignmentDate = new Date(parseInt(dateParts[0], 10), parseInt(dateParts[1], 10) - 1, parseInt(dateParts[2], 10));
      const today = new Date();
      today.setHours(0,0,0,0);
      assignmentDate.setHours(0,0,0,0);
      if (assignmentDate < today) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Invalid Date',
          text: 'You cannot select a past date.'
        });
        return;
      }
      const dayOfWeek = assignmentDate.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) {
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Invalid Date',
          text: 'Assignments cannot be submitted on weekends.'
        });
        return;
      }
      try {
        const response = await axios.post(backendUrl('/custom_assignments'), this.newTask, {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        const taskToAdd = response.data;
        taskToAdd.user_task = true;
        this.assignments.push(taskToAdd);
        this.closeAddTaskModal();
      } catch (error) {
        console.error('Error adding custom task:', error);
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while adding the task. Please try again.'
        });
      }
    },

    async deleteAssignment(assignment) {
      const result = await Swal.fire({
        ...this.swalDarkModeConfig,
        icon: 'warning',
        title: 'Delete Assignment?',
        text: 'Are you sure you want to delete this assignment?',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it',
        cancelButtonText: 'Cancel',
        customClass: {
          ...this.swalDarkModeConfig.customClass,
          confirmButton: 'bg-red-600 hover:bg-red-700 text-white font-semibold py-2 px-4 rounded-lg'
        }
      });

      if (!result.isConfirmed) return;
      try {
        await axios.delete(backendUrl(`/custom_assignments/${assignment.assignment_id}`), {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        this.assignments = this.assignments.filter(a => a.assignment_id !== assignment.assignment_id);
      } catch (error) {
        console.error('Error deleting assignment:', error);
        Swal.fire({
          ...this.swalDarkModeConfig,
          icon: 'error',
          title: 'Error',
          text: 'An error occurred while deleting the assignment. Please try again.'
        });
      }
    },

    async updateStatus(assignment, status) {
      assignment.status = status;
      try {
        await axios.post(backendUrl('/assignment_statuses'), {
          assignment_id: assignment.assignment_id, status
        }, { headers: { Authorization: `Bearer ${this.token}` }});
        await this.fetchAssignments(false);
      } catch (error) {
        console.error('Error updating assignment status:', error);
      }
    },

    truncateText(text, length) {
      return text.length <= length ? text : text.substring(0, length) + '...';
    },

    pluralize(count, s, p) {
      return count === 1 ? s : p;
    },

    extractToken() {
      const storedToken = getActiveAuthToken();
      this.token = storedToken || null;
    },

    isTokenExpired(token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const expiry = payload.exp;
        const now = Math.floor(new Date().getTime() / 1000);
        return now > expiry;
      } catch (error) {
        console.error('Invalid token format:', error);
        return true;
      }
    },

    async fetchUsername() {
      if (!this.token || this.isTokenExpired(this.token)) return;
      try {
        const response = await axios.get(backendUrl('/username'), {
          headers: { Authorization: `Bearer ${this.token}` },
        });
        this.fname = response.data.fname || response.data.username;
      } catch (error) {
        console.error('Error fetching username:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      }
    },

    async fetchAssignments() {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        const start_date = this.formatApiDate(new Date());
        const end_date = this.formatApiDate(new Date(Date.now() + 31 * 24 * 60 * 60 * 1000));
        const response = await axios.get(backendUrl('/assignments'), {
          params: {start_date, end_date},
          headers: {Authorization: `Bearer ${this.token}`},
        });
        // Clean HTML tags from assignment fields
        this.assignments = response.data.value.map(assignment => ({
          ...assignment,
          short_description: this.cleanField(assignment.short_description),
          long_description: this.cleanField(assignment.long_description),
          section_name: this.cleanField(assignment.section_name)
        }));
        this.$nextTick(() => {
          this.queueSchedulePrefetch();
        });
      } catch (error) {
        console.error('Error fetching assignments:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      }
    },

    async fetchSchedules() {
      if (!this.token || this.isTokenExpired(this.token)) {
        beginLogin({ originalUrl: window.location.href });
        return;
      }
      try {
        // On weekends, fetch next school day's schedule
        const today = new Date();
        const dayOfWeek = today.getDay();
        const targetDate = (dayOfWeek === 0 || dayOfWeek === 6) ? this.nextSchoolDay : today;

        const start_date = this.formatApiDate(targetDate);
        const end_date = this.formatApiDate(targetDate);
        const response = await axios.get(backendUrl('/schedule'), {
          params: {start_date, end_date},
          headers: {Authorization: `Bearer ${this.token}`},
        });
        this.schedules = response.data.value;

        // Cache the schedules for later lookup (class start times in assignment modal)
        this.schedulesCache[start_date] = response.data.value || [];
      } catch (error) {
        console.error('Error fetching schedule:', error);
        if (error.response && error.response.status === 401) {
          beginLogin({ originalUrl: window.location.href });
        }
      }
    },

    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },

    formatDueDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);

      const dayOfWeek = date.toLocaleDateString('en-US', {
        weekday: 'short',
        timeZone: 'America/New_York'
      });
      const monthDay = date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York'
      });

      // Check if it's tomorrow (compare in Eastern timezone)
      const today = new Date();
      const todayET = new Date(today.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      todayET.setHours(0, 0, 0, 0);

      const dueDateET = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
      dueDateET.setHours(0, 0, 0, 0);

      const tomorrow = new Date(todayET);
      tomorrow.setDate(todayET.getDate() + 1);

      if (dueDateET.getTime() === tomorrow.getTime()) {
        return `Tomorrow (${dayOfWeek}, ${monthDay})`;
      }

      return `${dayOfWeek}, ${monthDay}`;
    },

    formatTime(timeString) {
      // Parse ISO string and display in Eastern timezone
      const date = new Date(timeString);
      return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'America/New_York'  // Force Eastern timezone display
      });
    },

    formatModalDueDate(dateString) {
      if (!dateString) return '';
      // Parse the date string (YYYY-MM-DD or ISO format)
      const datePart = dateString.split('T')[0];
      const [year, month, day] = datePart.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'America/New_York'
      });
    },

    formatApiDate(date) {
      // Format date in Eastern timezone for API calls
      const easternFormatter = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/New_York',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
      // en-CA gives YYYY-MM-DD format
      return easternFormatter.format(date);
    },
  },

  mounted() {
    this.isLoading = true;
    this.extractToken();

    if (this.token && !this.isTokenExpired(this.token)) {
      const params = new URLSearchParams(window.location.search);
      console.log('[TEST] URLSearchParams:', [...params.entries()]);
      const forcedISO = params.get('testDate'); // optional override for dining only
      console.log('[TEST] forcedISO param:', forcedISO);

      // On weekends (Eastern time), fetch next school day's data
      const today = new Date();
      const targetDate = this.isWeekend ? this.nextSchoolDay : today;

      const isISO = /^\d{4}-\d{2}-\d{2}$/.test(forcedISO || '');
      const dateToUse = isISO ? forcedISO : this.formatApiDate(targetDate);

      console.group('[Dashboard] initial load');
      console.log('isWeekend (Eastern):', this.isWeekend);
      console.log('dateToUse (for dining):', dateToUse);
      Promise.all([
        this.fetchUsername(),
        this.fetchAssignments(),
        this.fetchSchedules(),
        this.fetchDining(dateToUse),
      ])
          .catch((error) => {
            console.error('[Dashboard] error during initial load:', error);
          })
          .finally(() => {
            console.groupEnd();
            this.isLoading = false;
            // Check if user has seen the current changelog version
            this.checkChangelogVersion();
          });
    } else {
      beginLogin({ originalUrl: window.location.href });
    }
  },

  beforeUnmount() {
    if (this.anyModalOpen) this.unlockBodyScroll();
  },
};
</script>

<style scoped>
/* Spinner Customization */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Responsive Adjustments */
@media (max-width: 640px) {
  h2 {
    font-size: 1.5rem;
  }

  h3 {
    font-size: 1.25rem;
  }

  .px-4 {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}

/* Table Styling */
table th, table td {
  text-align: left;
  padding-left: 0.5rem;
}

table th {
  font-weight: bold;
  padding: 8px 0.5rem;
}

table td {
  padding: 12px 0.5rem;
}

table {
  width: 100%;
  border-spacing: 0;
}

tr:nth-child(even) {
  background-color: #f9fafb;
}

/* Scrollbar Styling */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #4f46e5;
}

.dark::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.dark::-webkit-scrollbar-thumb:hover {
  background: #6366f1;
}

/* Modal description scrollbar */
.desc-scroll::-webkit-scrollbar {
  width: 6px;
}

.desc-scroll::-webkit-scrollbar-thumb {
  background: #6366f1;
  border-radius: 4px;
}

.dark .desc-scroll::-webkit-scrollbar-thumb {
  background: #818cf8;
}

.desc-scroll {
  -webkit-overflow-scrolling: touch; /* momentum scrolling on iOS */
  overscroll-behavior: contain; /* prevent background scroll chaining */
  touch-action: pan-y; /* signal vertical scrolling */
}

/* Make the date picker icon visible in dark mode */
.dark input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(1);
}

/* Changelog modal animations */
.changelog-modal {
  animation: modalSlideIn 0.25s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.feature-card {
  animation: cardFadeIn 0.3s ease-out backwards;
}

.feature-card:nth-child(1) { animation-delay: 0.05s; }
.feature-card:nth-child(2) { animation-delay: 0.1s; }
.feature-card:nth-child(3) { animation-delay: 0.15s; }

@keyframes cardFadeIn {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Changelog content scrollbar */
.changelog-content::-webkit-scrollbar {
  width: 6px;
}

.changelog-content::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.dark .changelog-content::-webkit-scrollbar-thumb {
  background: #475569;
}

.changelog-content {
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
</style>
